import React, { useState } from 'react';
import api from '../api';

const ChangePasswordForm = ({ onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState([]);
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });

    const { currentPassword, newPassword, confirmPassword } = formData;

    const onChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setErrors([]); // Clear errors when user types
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords({
            ...showPasswords,
            [field]: !showPasswords[field]
        });
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setErrors([]);

        // Frontend validation
        if (newPassword !== confirmPassword) {
            setErrors([{ msg: 'Mật khẩu mới và xác nhận mật khẩu không khớp' }]);
            return;
        }

        if (newPassword.length < 6) {
            setErrors([{ msg: 'Mật khẩu mới phải có ít nhất 6 ký tự' }]);
            return;
        }

        if (currentPassword === newPassword) {
            setErrors([{ msg: 'Mật khẩu mới phải khác với mật khẩu hiện tại' }]);
            return;
        }

        try {
            setLoading(true);
            const res = await api.put('/users/change-password', {
                currentPassword,
                newPassword
            });

            // Success
            if (onSuccess) {
                onSuccess(res.data.msg);
            }

            // Reset form
            setFormData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
        } catch (err) {
            const errorMessages = err.response?.data?.errors || [{ msg: 'Có lỗi xảy ra khi đổi mật khẩu' }];
            setErrors(errorMessages);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="rounded-2xl bg-slate-50 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Đổi mật khẩu</h3>

            {errors.length > 0 && (
                <div className="mb-4 rounded-xl bg-red-50 border border-red-200 p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <i className="fas fa-exclamation-circle text-red-400" aria-hidden></i>
                        </div>
                        <div className="ml-3">
                            <div className="text-sm text-red-700">
                                {errors.map((error, index) => (
                                    <p key={index}>{error.msg}</p>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <form onSubmit={onSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                        <i className="fas fa-lock mr-2" aria-hidden></i>
                        Mật khẩu hiện tại
                    </label>
                    <div className="relative">
                        <input
                            type={showPasswords.current ? 'text' : 'password'}
                            name="currentPassword"
                            value={currentPassword}
                            onChange={onChange}
                            required
                            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 pr-12 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                            placeholder="Nhập mật khẩu hiện tại"
                        />
                        <button
                            type="button"
                            onClick={() => togglePasswordVisibility('current')}
                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
                        >
                            <i className={`fas ${showPasswords.current ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden></i>
                        </button>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                        <i className="fas fa-key mr-2" aria-hidden></i>
                        Mật khẩu mới
                    </label>
                    <div className="relative">
                        <input
                            type={showPasswords.new ? 'text' : 'password'}
                            name="newPassword"
                            value={newPassword}
                            onChange={onChange}
                            required
                            minLength={6}
                            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 pr-12 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                            placeholder="Nhập mật khẩu mới (tối thiểu 6 ký tự)"
                        />
                        <button
                            type="button"
                            onClick={() => togglePasswordVisibility('new')}
                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
                        >
                            <i className={`fas ${showPasswords.new ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden></i>
                        </button>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                        <i className="fas fa-check-circle mr-2" aria-hidden></i>
                        Xác nhận mật khẩu mới
                    </label>
                    <div className="relative">
                        <input
                            type={showPasswords.confirm ? 'text' : 'password'}
                            name="confirmPassword"
                            value={confirmPassword}
                            onChange={onChange}
                            required
                            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 pr-12 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                            placeholder="Nhập lại mật khẩu mới"
                        />
                        <button
                            type="button"
                            onClick={() => togglePasswordVisibility('confirm')}
                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
                        >
                            <i className={`fas ${showPasswords.confirm ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden></i>
                        </button>
                    </div>
                </div>

                <div className="flex gap-3 pt-2">
                    <button
                        type="submit"
                        disabled={loading}
                        className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow transition hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <>
                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"></span>
                                Đang đổi...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-save" aria-hidden></i>
                                Đổi mật khẩu
                            </>
                        )}
                    </button>
                    {onCancel && (
                        <button
                            type="button"
                            onClick={onCancel}
                            className="rounded-xl border border-slate-200 bg-white px-6 py-3 text-sm font-medium text-slate-700 shadow transition hover:bg-slate-50"
                        >
                            Hủy
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default ChangePasswordForm;